import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useApi from "@/hooks/useApi";
import Image from "next/image";
import { NumericFormat } from "react-number-format";
import { isMobileDevice } from "@/libs/utils";

const LoanCalculatorBanner = ({ available = false }) => {
  return (
    <div
      className={`rounded py-3 px-2 w-full flex ${
        available
          ? "text-[#13a97b] bg-[#edfffa]"
          : "text-[#414141] bg-[#ebebeb]"
      }`}
    >
      <Image
        src={
          available
            ? "/assets/ellipse-green-8.svg"
            : "/assets/ellipse-gray-8.svg"
        }
        width={8}
        height={8}
        alt="banner icon"
      />
      <span className="text-sm ml-2">
        Vehicle {available ? "available" : "not available"} for financing
      </span>
    </div>
  );
};

interface Props {
  initialVehiclePrice: number;
  loanable?: boolean;
  onMonthlyChange: (total: number) => any;
  onApplyClick: () => any;
}

interface LoanForm {
  vehiclePrice: number;
  downPayment: number;
  interestRate: number;
  loanTerm: number;
}

const LoanFormSchema: yup.ObjectSchema<LoanForm> = yup.object().shape({
  vehiclePrice: yup.number().required(),
  downPayment: yup.number().required(),
  interestRate: yup.number().required(),
  loanTerm: yup.number().required(),
});

const LoanCalculator = ({
  initialVehiclePrice,
  loanable = false,
  onMonthlyChange = () => {},
  onApplyClick = () => {},
}: Props) => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { control, watch, setValue } = useForm<LoanForm>({
    resolver: yupResolver(LoanFormSchema),
    defaultValues: {
      vehiclePrice: 0,
      downPayment: 0,
      interestRate: 0,
      loanTerm: 72,
    },
  });

  const formData = watch();
  const { getLoanRates } = useApi();
  const { vehiclePrice, downPayment, interestRate, loanTerm } = formData;
  const loanAmount = Number(vehiclePrice) - Number(downPayment);
  const interest = (loanAmount * (interestRate * 0.01)) / loanTerm;
  const monthlyPayment = Number(loanAmount / loanTerm + interest) || 0;

  const initialize = async () => {
    if (loanable) {
      const { data } = await getLoanRates(false);

      const rate72mo = data.find(
        ({ rate_name }) => rate_name.indexOf("72") !== -1
      )?.rate_apr;
      const defaultInterestRate = Number(rate72mo || 0);
      setValue("interestRate", defaultInterestRate);
    }
    setIsMobile(isMobileDevice());
  };

  useEffect(() => {
    initialize();
  }, [loanable]);

  useEffect(() => {
    const downPayment = initialVehiclePrice * 0.1;
    setValue("downPayment", downPayment);
    setValue("vehiclePrice", initialVehiclePrice);
  }, [initialVehiclePrice]);

  useEffect(() => {
    if (onMonthlyChange) onMonthlyChange(monthlyPayment);
  }, [monthlyPayment]);

  return (
    <div className="md:max-w-[820px] sm:w-full rounded-lg bg-[#f7f9fc] p-[36px] px-[22px]">
      <div className="p-[32px] px-[36px] rounded-lg border border-[#c4c4c4] bg-white">
        <div className="flex justify-between">
          <div className="text-black text-xl font-semibold leading-[20px] flex items-center">
            <Image
              alt="dollar icon"
              src="/assets/dollar-circle-green.svg"
              height={28}
              width={27}
            />
            <span className="ml-2">Get in-app Financing</span>
          </div>

          {!isMobile && (
            <div>
              <LoanCalculatorBanner available={loanable} />
            </div>
          )}
        </div>
        <div className="text-[#212529] font-normal mt-2">
          <div className="text-base mt-4">Estimated monthly payment</div>
          <div className="text-[40px] mt-3">
            <span className="font-semibold">
              ${Number(monthlyPayment.toFixed(0)).toLocaleString()}
            </span>
            <span className="font-bold text-xl">/mo</span>
          </div>
          <div className="text-sm mt-2">
            for {formData.loanTerm / 12}{" "}
            {formData.loanTerm > 12 ? "years" : "year"}
          </div>

          {isMobile && (
            <div className="w-full mt-3">
              <LoanCalculatorBanner available={loanable} />
            </div>
          )}
        </div>

        <form className="form mt-3">
          <div className="md:flex">
            <div className="form-group w-full md:w-1/2 mb-3 mb-4">
              <label className="text-[#063829] text-[16px] font-bold leading-[24px]">
                Vehicle Price
              </label>
              <Controller
                render={({ field: { onChange, name, value } }) => (
                  <NumericFormat
                    className="form-control mt-2"
                    thousandSeparator={true}
                    name={name}
                    value={value}
                    decimalScale={0}
                    onValueChange={(v) => {
                      onChange(v.floatValue);
                    }}
                  />
                )}
                name="vehiclePrice"
                control={control}
              />
            </div>
            <div className="form-group w-full md:w-1/2 mb-3 md:ml-3 mb-4">
              <label className="text-[#063829] text-[16px] font-bold leading-[24px]">
                Down Payment
              </label>
              <Controller
                render={({ field: { onChange, name, value } }) => (
                  <NumericFormat
                    className="form-control mt-2"
                    thousandSeparator={true}
                    name={name}
                    value={value}
                    decimalScale={0}
                    onValueChange={(v) => {
                      onChange(v.floatValue);
                    }}
                  />
                )}
                name="downPayment"
                control={control}
              />
            </div>
          </div>
          <div className="md:flex">
            <div className="form-group w-full md:w-1/2 mb-3 mb-4">
              <label className="text-[#063829] text-[16px] font-bold leading-[24px]">
                Credit Score
              </label>
              <select className="form-control form-control-select mt-2">
                <option value="Good(670-739)">Good(739-670)</option>
                <option value="Very Good(740-799)">Very Good(799-740)</option>
                <option value="Excellent(800-850)">Excellent(850-800)</option>
              </select>
            </div>
            <div className="form-group w-full md:w-1/2 mb-3 md:ml-3 mb-4">
              <label className="text-[#063829] text-[16px] font-bold leading-[24px]">
                Interest Rate
              </label>
              <Controller
                render={({ field: { onChange, name, value } }) => (
                  <NumericFormat
                    className="form-control mt-2"
                    suffix="%"
                    thousandSeparator={true}
                    name={name}
                    value={value}
                    decimalScale={2}
                    onValueChange={(v) => {
                      onChange(v.floatValue);
                    }}
                    isAllowed={(v) => {
                      const { floatValue = 0 } = v;
                      return floatValue >= 0 && floatValue <= 100;
                    }}
                  />
                )}
                name="interestRate"
                control={control}
              />
            </div>
          </div>
          <div className="md:flex">
            <div className="form-group w-full md:w-1/2 mb-3 mb-4">
              <label className="text-[#063829] text-[16px] font-bold leading-[24px]">
                Loan Term
              </label>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <select
                    className="form-control form-control-select mt-2"
                    value={value}
                    onChange={(e) => {
                      onChange(Number(e.target.value));
                    }}
                  >
                    <option value="12">12 months</option>
                    <option value="24">24 months</option>
                    <option value="36">36 months</option>
                    <option value="48">48 months</option>
                    <option value="60">60 months</option>
                    <option value="72">72 months</option>
                  </select>
                )}
                name="loanTerm"
                control={control}
              />
            </div>

            {/* spacing */}
            <div className="form-group w-full md:w-1/2 mb-3 md:ml-3 mb-4"></div>
          </div>
        </form>

        <div className="md:flex items-center justify-between mt-3">
          <div className="w-full md:w-1/2">
            <div className="text-[#2e3b54] text-[16px] font-bold leading-[36px]">
              Get financing for this vehicle
            </div>
            <div className="text-[#808080] text-[12px] font-medium leading-[24px]">
              Loan services provided by USALLIANCE Financial
            </div>
          </div>

          <div className="flex justify-center w-full md:w-1/2 mt-3 md:mt-0">
            <button
              className="btn-primary w-full km:w-auto"
              onClick={onApplyClick}
            >
              Apply for a loan
            </button>
          </div>
        </div>

        <div className="text-[#808080] text-[10px] font-normal leading-[16px] mt-3">
          Monthly payment estimates are for informational purposes and do not
          represent a financing offer. Not all customers will qualify. All
          decisions related to financing are at the sole discretion of our
          banking partner. All interest rates and terms provided by this
          calculator are estimates and may not reflect current rates or terms
          available in the market.
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;
